import { apiSlice } from './apiSlice';

export interface InitiateMultipartRequest {
  fileName: string;
  fileType: string;
  folder: string;
}

export interface InitiateMultipartResponse {
  key: string;
  uploadId: string;
  message?: string;
  error?: string;
  statusCode?: number;
}

export interface PresignedUrlRequest {
  key: string;
  uploadId: string;
  partNumber: number;
}

export interface PresignedUrlResponse {
  url?: string;
  signedUrl?: string;
  message?: string;
  error?: string;
  statusCode?: number;
}

export interface CompleteMultipartRequest {
  key: string;
  uploadId: string;
  parts: Array<{
    ETag: string;
    PartNumber: number;
  }>;
}

export interface CompleteMultipartResponse {
  success?: boolean;
  videoUrl?: string;
  fileUrl?: string;
  message?: string;
  error?: string;
  statusCode?: number;
}

export interface AbortMultipartRequest {
  key: string;
  uploadId: string;
}

export interface AbortMultipartResponse {
  message?: string;
  error?: string;
  statusCode?: number;
}

export interface SaveVideoRequest {
  title: string;
  videoUrl: string;
  s3Key: string;
  fileSize: number;
  status: string; // e.g. "Pending", "Success", "Failed"
}

export interface VideoData {
  id: string;
  title: string;
  videoUrl: string;
  s3Key: string;
  fileSize: number;
  videoStatus: string;
  createdAt: string;
  updatedAt: string;
  localUrl?: string; // Optional local URL for demo mode playback
}

export interface SaveVideoResponse {
  success: boolean;
  message: string;
  data: VideoData;
}

export interface GetVideoResponse {
  success: boolean;
  data: VideoData;
}

export interface DeleteVideoResponse {
  message: string;
  error?: string;
  statusCode?: number;
}

export interface GetS3VideosRequest {
  skip: number;
  take: number;
}

export interface GetS3VideosResponse {
  count: number;
  skip: number;
  take: number;
  data: VideoData[];
}

export const videoGenerationsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    initiateMultipart: builder.mutation<InitiateMultipartResponse, InitiateMultipartRequest>({
      query: (body) => ({
        url: '/video-generations/multipart/initiate',
        method: 'POST',
        body,
      }),
    }),
    getPresignedUrl: builder.mutation<PresignedUrlResponse, PresignedUrlRequest>({
      query: (body) => ({
        url: '/video-generations/multipart/presigned-url',
        method: 'POST',
        body,
      }),
    }),
    completeMultipart: builder.mutation<CompleteMultipartResponse, CompleteMultipartRequest>({
      query: (body) => ({
        url: '/video-generations/multipart/complete',
        method: 'POST',
        body,
      }),
    }),
    abortMultipart: builder.mutation<AbortMultipartResponse, AbortMultipartRequest>({
      query: (body) => ({
        url: '/video-generations/multipart/abort',
        method: 'POST',
        body,
      }),
    }),
    saveVideo: builder.mutation<SaveVideoResponse, SaveVideoRequest>({
      query: (body) => ({
        url: '/video-generations/save-video',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    getVideo: builder.query<GetVideoResponse, string>({
      query: (id) => ({
        url: `/video-generations/${id}`,
        method: 'GET',
      }),
    }),
    deleteVideo: builder.mutation<DeleteVideoResponse, string>({
      query: (id) => ({
        url: `/video-generations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    getS3Videos: builder.query<GetS3VideosResponse, GetS3VideosRequest>({
      query: ({ skip, take }) => ({
        url: '/video-generations/s3-videos',
        method: 'GET',
        params: { skip, take },
      }),
      providesTags: ['User'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useInitiateMultipartMutation,
  useGetPresignedUrlMutation,
  useCompleteMultipartMutation,
  useAbortMultipartMutation,
  useSaveVideoMutation,
  useGetVideoQuery,
  useLazyGetVideoQuery,
  useDeleteVideoMutation,
  useGetS3VideosQuery,
  useLazyGetS3VideosQuery,
} = videoGenerationsApi;
